// © 2026 AttainIP Engineering
import { Fragment, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ChevronRight, ChevronDown } from 'lucide-react';
import { fetchFeedback } from '@/api/queries';
import { Card } from '@/components/Card';
import { FeedbackDetail } from '@/components/FeedbackDetail';

const RATING_FILTERS = [
  { value: 0, label: 'All' },
  { value: 5, label: '★★★★★' },
  { value: 4, label: '★★★★' },
  { value: 3, label: '★★★' },
  { value: 2, label: '★★' },
  { value: 1, label: '★' },
];

export function FeedbackPage() {
  const [search, setSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState(0);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['feedback'],
    queryFn: () => fetchFeedback({ limit: 200 }),
  });

  const feedback = data?.data || [];

  const filtered = useMemo(() => {
    let items = feedback;
    if (ratingFilter > 0) {
      items = items.filter((fb: any) => fb.rating === ratingFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter((fb: any) =>
        (fb.comment || '').toLowerCase().includes(q) ||
        (fb.user_name || '').toLowerCase().includes(q) ||
        (fb.user_email || '').toLowerCase().includes(q) ||
        (fb.resource_id || '').toLowerCase().includes(q)
      );
    }
    return items;
  }, [feedback, search, ratingFilter]);

  // Rating distribution (from all feedback, not filtered)
  const dist = [0, 0, 0, 0, 0];
  feedback.forEach((fb: any) => {
    if (fb.rating >= 1 && fb.rating <= 5) dist[fb.rating - 1]++;
  });
  const avg = feedback.length > 0
    ? (feedback.reduce((s: number, fb: any) => s + (fb.rating || 0), 0) / feedback.length).toFixed(1)
    : '—';

  if (isLoading) return <p className="text-gray-500">Loading feedback...</p>;

  return (
    <div>
      <h1 className="text-xl font-bold mb-6">Feedback ({feedback.length})</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card title="Summary">
          <div className="flex items-center gap-6">
            <div>
              <p className="text-3xl font-bold">{avg}</p>
              <p className="text-xs text-gray-400">Average rating</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{feedback.length}</p>
              <p className="text-xs text-gray-400">Total responses</p>
            </div>
          </div>
        </Card>
        <Card title="Distribution">
          <div className="space-y-1">
            {[5, 4, 3, 2, 1].map((star) => (
              <div key={star} className="flex items-center gap-2 text-sm">
                <span className="w-4 text-right">{star}</span>
                <span className="text-yellow-500">★</span>
                <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
                  <div
                    className="bg-yellow-400 h-full rounded"
                    style={{ width: feedback.length > 0 ? `${(dist[star - 1] / feedback.length) * 100}%` : '0%' }}
                  />
                </div>
                <span className="text-xs text-gray-400 w-6">{dist[star - 1]}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="flex gap-2 mb-4">
        {RATING_FILTERS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setRatingFilter(value)}
            className={`px-3 py-1 rounded text-xs font-medium ${
              ratingFilter === value ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by comment, user, or resource ID..."
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <Card title={`Feedback (${filtered.length})`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 font-medium w-6"></th>
              <th className="pb-2 font-medium">Rating</th>
              <th className="pb-2 font-medium">Comment</th>
              <th className="pb-2 font-medium">User</th>
              <th className="pb-2 font-medium">Resource</th>
              <th className="pb-2 font-medium">Date</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((fb: any) => {
              const isOpen = !!expanded[fb.id];
              const toggle = () => setExpanded((s) => ({ ...s, [fb.id]: !s[fb.id] }));
              return (
                <Fragment key={fb.id}>
                  <tr
                    className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                    onClick={toggle}
                  >
                    <td className="py-2 text-gray-400">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </td>
                    <td className="py-2 text-yellow-500">{'★'.repeat(fb.rating)}{'☆'.repeat(5 - fb.rating)}</td>
                    <td className="py-2 max-w-xs truncate">{fb.comment || '—'}</td>
                    <td className="py-2 text-xs">{fb.user_name || fb.user_email || fb.user_id?.slice(0, 8) + '...'}</td>
                    <td className="py-2 font-mono text-xs text-gray-400">{fb.resource_id}</td>
                    <td className="py-2 text-xs text-gray-400">{new Date(fb.created_at).toLocaleDateString()}</td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b last:border-0 bg-gray-50">
                      <td></td>
                      <td colSpan={5} className="py-3 pr-4">
                        <FeedbackDetail fb={fb} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p className="text-gray-400 text-sm py-4 text-center">
            {search || ratingFilter ? 'No matching feedback' : 'No feedback found'}
          </p>
        )}
      </Card>
    </div>
  );
}
